import React, { useEffect, useContext } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Profile from "./pages/MyProfile";
import MyStoriesPage from "./pages/MyStoriesPage.js";
import MyStoriesFormPage from "./pages/MystoriesFormPage.js";
import StoryDetailsPage from "./pages/StoryDetailsPage";
import ChapterEditorPage from "./pages/ChapterEditorPage";
import EditorialPage from "./pages/EditorialPage";
import ProtectedRoute from "./components/ProtectedRoute";
import { ThemeModeContext } from "./theme/ThemeContext";
import { useSharedAuth } from "./contexts/AuthProvider"; // ✅ Use shared auth, not `useAuth()`
import StoryFormEditorial from "./components/StoryFormEditorial";
import StoryDetailsPageEditorial from "./pages/StoryDetailsPageEditorial.js";
import ChapterEditorPageEditorial from "./pages/ChapterEditorPageEditorial";
import EditorialStoriesDirectoryPage from "./pages/EditorialStoriesDirectoryPage";
import EditorialStoriesLayout from "./pages/EditorialStoriesLayout";
import ClientAuth from "./pages/ClientAuth"; // Handles Firebase custom token login
import { LinearProgress, Box } from "@mui/material";

import { useUser } from "./contexts/UserContext";
import ChooseUsernameScreen from "./components/ChooseUsernameScreen";

import AICompanion from "./components/AICompanion";

const AppContent = () => {
  return <AppContentInner />; // ✅ Removed extra ThemeContextProvider here
};

const AppContentInner = () => {

  //console.log("AppContentInner: Rendering...");
  //console.trace();

  const { mode, toggleTheme } = useContext(ThemeModeContext);
  const { user, customClaims, loading } = useSharedAuth(); // ✅ No forced refresh
  const { customData, loading: userDataLoading } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {

    //console.log("AppContentInner: Checking authentication...");
    //console.trace();

    if (!loading && customClaims?.isEditor && location.pathname === "/") {
      navigate("/editorial", { replace: true });
    }
  }, [loading, customClaims?.isEditor, location.pathname, navigate]);

  if (loading || userDataLoading) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 1300, // Ensure it stays above everything
        }}
      >
        <LinearProgress />
      </Box>
    );
  }

  if (user && customData?.Username === `User${user.uid}`) {
    return <ChooseUsernameScreen />;
  }

  const getCurrentStoryContext = () => {
    const path = location.pathname;
    const match = path.match(/\/(editorial|mystories)\/([^/]+)/);
    const storyId = match?.[2];
    return {
      storyId: storyId || null,
      storySummary: "" // you could enhance this with context if available
    };
  };

  return (
    <>
      <Navbar user={user} toggleTheme={toggleTheme} mode={mode} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />

        {/* Editorial Dashboard: Editors Only */}
        <Route path="/editorial" element={<ProtectedRoute editorOnly><EditorialPage /></ProtectedRoute>} />
        <Route path="/editorial/newstory" element={<ProtectedRoute editorOnly><StoryFormEditorial /></ProtectedRoute>} />
        {/* Nested routes for /editorial/stories */}
        <Route path="/editorial/stories" element={<ProtectedRoute editorOnly><EditorialStoriesLayout /></ProtectedRoute>}>
          {/* Directory or listing page */}
          <Route index element={<EditorialStoriesDirectoryPage />} />
          {/* Specific story details */}
          <Route path=":storyID" element={<StoryDetailsPageEditorial />} />
          {/* Specific chapter editor */}
          <Route path=":storyID/:chapterIndex" element={<ChapterEditorPageEditorial />} />
        </Route>

        {/* MyStories Section */}
        <Route path="/mystories" element={<ProtectedRoute><MyStoriesPage /></ProtectedRoute>} />
        <Route path="/mystories/new" element={<ProtectedRoute><MyStoriesFormPage /></ProtectedRoute>} />
        <Route path="/mystories/:storyID" element={<ProtectedRoute><StoryDetailsPage /></ProtectedRoute>} />
        <Route path="/mystories/:storyID/:chapterIndex" element={<ProtectedRoute><ChapterEditorPage /></ProtectedRoute>} />

        { /* Client Auth: Handles Firebase custom token login */}
        <Route path="/sessionLogin" element={<ClientAuth />} />

        {/* Catch-all route: Redirect to "/" for unmatched routes */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
      <AICompanion getContext={getCurrentStoryContext} />

    </>
  );
};

export default AppContent;
